import { Inject, Injectable, NgZone } from '@angular/core';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';

import { MapCoords, MapIcon, MapLocation, MapMarker } from '../../domain/models/map.models';
import { MapServicesProviderInterface } from '../../domain/ports/secondary/map-services-provider.interface';
import { MapService } from '../../map.service';

import { Coordinates } from '@api/geolocation/models/coordinates.model';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { LoadLibrary } from '@core/infrastructure/state/core-commands.actions';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import { MapPublicUseCases } from '@shared/map';
import { determineModeIconUseCase } from '@shared/map/determine-mode-icon.use-case';

const GOOGLE_MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js';
const GOOGLE_MAPS_API_VERSION = '3';

@Injectable()
export class GoogleService implements MapServicesProviderInterface {
    public scriptSrc$ = this.store.select(getStoreAndLanguage).pipe(
        map(({ countryCode, languageCode }) => {
            const url = new URL(GOOGLE_MAPS_API_URL);

            const queryParams = new URLSearchParams({
                key: this.window.inditex?.iGoogleMapsPlatformKey,
                v: GOOGLE_MAPS_API_VERSION,
                region: countryCode,
                language: languageCode,
            });

            url.search = queryParams.toString();

            return url.toString();
        }),
    );

    private map: google.maps.Map;

    private markerCluster: any;

    // eslint-disable-next-line deprecation/deprecation
    private markers: google.maps.Marker[] = [];

    private currentCoords: google.maps.LatLng;

    private readonly mapEventSubject$: Subject<any> = new Subject();

    public mapEvent$ = this.mapEventSubject$.asObservable();

    constructor(
        private readonly mapService: MapService,
        private readonly store: Store,
        private readonly zone: NgZone,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: CorePlatformService,
    ) {
    }

    // eslint-disable-next-line no-secrets/no-secrets
    // TODO ECOMDUTI-21024 eliminar inditex.iGoogleMapsPlatformKey
    public loadMapLibraries(): void {
        if (this.platformService.isBrowser()) {
            this.store.dispatch(
                LoadLibrary({
                    library: 'google',
                    src: `https://maps.googleapis.com/maps/api/js?key=${this.window.inditex?.iGoogleMapsPlatformKey}&amp;v=3`,
                }),
            );
        }
    }

    public setCenter(coords: MapCoords): void {
        const gCoords = new google.maps.LatLng(coords.lat, coords.lng);

        if (gCoords && this.map && !this.map?.getCenter().equals(gCoords)) {
            this.map.setCenter(gCoords);
            this.currentCoords = this.map.getCenter();
        }

        if (coords.zoom) {
            this.setZoom(coords.zoom);
        }
    }

    public setZoom(zoom: number): void {
        if (this.map) {
            this.map.setZoom(zoom);
        }
    }

    private setMapEvent(event: { kind: string; data: any }): void {
        this.mapEventSubject$.next(event);
    }

    public initMap(coords: MapCoords, container: HTMLElement): void {
        try {
            const gCoords = new google.maps.LatLng(coords.lat, coords.lng);

            this.map = new google.maps.Map(container, {
                center: gCoords,
                zoom: 10,
                disableDefaultUI: true,
                mapTypeId: 'duttiEspecial',
                minZoom: 6,
            });
        } catch (error) {
            ItxObservabilityCommands.sendObservabilityLog(error, 'initMap');
            console.error(error);
        }

        if (this.map) {
            this.currentCoords = this.map.getCenter();
            this.setMapTypes();
            this.setSizeOfIcons();
            this.setListeners();
            this.setMapEvent({ kind: 'idle', data: {} });
        }
    }

    private setMapTypes(): void {
        const mapType = new google.maps.StyledMapType(
            [
                {
                    featureType: 'administrative',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'off',
                        },
                    ],
                },
                {
                    featureType: 'landscape',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'simplified',
                        },
                        {
                            hue: '#0066ff',
                        },
                        {
                            saturation: 74,
                        },
                        {
                            lightness: 100,
                        },
                    ],
                },
                {
                    featureType: 'poi',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'simplified',
                        },
                    ],
                },
                {
                    featureType: 'poi.business',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'off',
                        },
                    ],
                },
                {
                    featureType: 'road',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'simplified',
                        },
                    ],
                },
                {
                    featureType: 'road.highway',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'off',
                        },
                        {
                            weight: 0.6,
                        },
                        {
                            saturation: -85,
                        },
                        {
                            lightness: 61,
                        },
                    ],
                },
                {
                    featureType: 'road.highway',
                    elementType: 'geometry',
                    stylers: [
                        {
                            visibility: 'on',
                        },
                    ],
                },
                {
                    featureType: 'road.arterial',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'off',
                        },
                    ],
                },
                {
                    featureType: 'road.local',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'on',
                        },
                    ],
                },
                {
                    featureType: 'transit',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'simplified',
                        },
                    ],
                },
                {
                    featureType: 'water',
                    elementType: 'all',
                    stylers: [
                        {
                            visibility: 'simplified',
                        },
                        {
                            color: '#5f94ff',
                        },
                        {
                            lightness: 26,
                        },
                        {
                            gamma: 5.86,
                        },
                    ],
                },
            ],
            {
                name: 'duttiEspecial',
            },
        );

        this.map.mapTypes.set('duttiEspecial', mapType);
    }

    private setListeners(): void {
        this.map.addListener('dragend', () => {
            this.zone.run(() => {
                this.setMapEvent({ kind: 'dragend', data: {} });
            });
        });
    }

    public lookIfBoundsChanged(): boolean {
        const mapCenter = this.map.getCenter();

        const p1 = {
            latitude: Number(mapCenter.lat()),
            longitude: Number(mapCenter.lng()),
        };

        const p2 = {
            latitude: Number(this.currentCoords.lat()),
            longitude: Number(this.currentCoords.lng()),
        };

        const distance = Number((MapPublicUseCases.calcDistanceBetweenUseCase(p1, p2) / 1000).toFixed(1));

        if (distance >= this.mapService.DISTANCE_TO_BOUNDS) {
            this.currentCoords = mapCenter;
            return true;
        }

        return false;
    }

    public clearMarkers(): void {
        this.zone.run(() => {
            if (this.markers?.length) {
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < this.markers.length; i++) {
                    this.markers[i].setMap(null);
                }
                this.markers.length = 0;
            }
        });
    }

    public setMarkers(
        locations: MapLocation[],
        shouldShowDisabledIcon: boolean,
    ): void {
        this.clearMarkers();

        this.markers = locations.map((location) => {
            const mapMarker: MapMarker = this.getMarkerFromLocation(location, shouldShowDisabledIcon);
            const marker = this.loadMarker(mapMarker, location);
            return marker;
        });

        this.updateMarkerCluster();
    }

    private getMarkerFromLocation(location: MapLocation, shouldShowDisabledIcon: boolean): MapMarker {
        const mapMarker: MapMarker = {
            position: {
                lat: Number.parseFloat(location.latitude.toString()),
                lng: Number.parseFloat(location.longitude.toString()),
            },
            icon: this.getBaseIcon(location, shouldShowDisabledIcon),
            title: location.name,
        };
        return mapMarker;
    }

    // eslint-disable-next-line deprecation/deprecation
    private searchMarker(id: string): google.maps.Marker {
        // eslint-disable-next-line deprecation/deprecation
        const foundMarker: google.maps.Marker = this.markers?.find((marker) => {
            const icon: any = marker.getIcon();
            return icon.id === id;
        });
        return foundMarker;
    }

    private setSizeOfIcons(): void {
        // Revisar si deberia añadirse los de droppoints
        this.mapService.markerIcons.unused.icon.scaledSize = new google.maps.Size(48, 48); // scaled size
        this.mapService.markerIcons.unused.icon.tag = 'unused';

        this.mapService.markerIcons.used.icon.scaledSize = new google.maps.Size(48, 48); // scaled size
        this.mapService.markerIcons.used.icon.tag = 'used';
    }

    /**
     * Función para añadir un marker al mapa con evento "click"
     */
    // eslint-disable-next-line deprecation/deprecation
    private loadMarker(mapMarker: MapMarker, location: MapLocation): google.maps.Marker {
        // eslint-disable-next-line deprecation/deprecation
        const marker = new google.maps.Marker({
            ...mapMarker,
            map: this.map,
        });

        marker.addListener('click', () => {
            // Ésta funcionalidad debe ser ejecutada en el Zone de Angular ya que ocurre fuera del scope de Angular
            this.zone.run(() => {
                if (!location.disabled) {
                    this.setMapEvent({ kind: 'clickMarker', data: { marker, location } });
                }
            });
        });

        return marker;
    }

    private getBaseIcon(location: MapLocation, shouldShowDisabledIcon: boolean): MapIcon {
        const mode = determineModeIconUseCase(location, shouldShowDisabledIcon);
        const url: string = this.mapService.getBaseIconUrl(mode, location.provider);
        const icon: MapIcon = {
            url,
            tag: mode,
            kind: location.provider ?? '',
            id: location.id,
        };

        return icon;
    }

    // eslint-disable-next-line deprecation/deprecation
    private getIconFromMarker(marker: google.maps.Marker): MapIcon {
        const markerIcon: any = marker.getIcon();
        const mode: string = markerIcon.tag === 'used' ? 'unused' : 'used';
        const icon: MapIcon = {
            url: '',
            tag: mode,
            kind: '',
            id: markerIcon.id,
        };

        icon.url = this.mapService.getMarkerIconUrl(mode, markerIcon);

        if (markerIcon.kind) {
            icon.kind = markerIcon.kind;
        }
        return icon;
    }

    public calculateBoundsChanged(): { center: number; radius: number } {
        const bounds: google.maps.LatLngBounds = this.map.getBounds();
        const center: any = bounds.getCenter();
        const ne: any = bounds.getNorthEast();
        const sw: any = bounds.getSouthWest();
        sw.lat = sw.lat();
        sw.lng = sw.lng();
        ne.lat = ne.lat();
        ne.lng = ne.lng();
        center.lat = center.lat();
        center.lng = center.lng();
        const radius = Math.max(50, Math.ceil(this.mapService.computeDistance(sw.lat, sw.lng, ne.lat, ne.lng) / 1.75));
        const data: any = { center, radius };
        return data;
    }

    public unsetSelectedMarker(): void {
        // eslint-disable-next-line deprecation/deprecation
        const found: google.maps.Marker = this.markers?.find((marker) => {
            const markerIcon: any = marker.getIcon();

            return markerIcon?.tag === 'used';
        });

        if (found) {
            const icon: MapIcon = this.getIconFromMarker(found);
            found.setIcon(icon);
        }
    }

    public toggleShopMarker(coords: MapCoords): void {
        const myCoords: google.maps.LatLng = new google.maps.LatLng(coords.lat, coords.lng);
        // eslint-disable-next-line deprecation/deprecation
        const found: google.maps.Marker = this.markers?.find((marker) => {
            return marker.getPosition().equals(myCoords);
        });
        if (found) {
            const icon: MapIcon = this.getIconFromMarker(found);
            found.setIcon(icon);
        }
    }

    public setSelectedStore(location: MapLocation): void {
        const coords: MapCoords = {
            lat: location.latitude,
            lng: location.longitude,
        };
        this.setCenter(coords);
        this.toggleShopMarker(coords);
        this.setZoom(16);
    }

    public geocodeFromCoordinates(coordinates: Coordinates): Observable<any> {
        return new Observable((observer) => {
            const geocoderRequest: google.maps.GeocoderRequest = {
                location: {
                    lat: +coordinates.latitude,
                    lng: +coordinates.longitude,
                },
            };
            this.getGeocoderResponse(geocoderRequest, observer);
        });
    }

    private updateMarkerCluster(): void {
        if (this.markerCluster) {
            this.zone.run(() => {
                this.markerCluster.clearMarkers();
            });
        }

        const renderer = {
            render: ({ count, position }) =>
                // eslint-disable-next-line deprecation/deprecation
                new google.maps.Marker({

                    // eslint-disable-next-line deprecation/deprecation
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                    label: {
                        text: String(count), color: '#ffffff', fontSize: '14px', fontWeight: 'bold', className: 'google-cluster',
                    },
                    position,
                    icon: {
                        url: this.mapService.getClusterInlineSvg('black'),
                        size: {
                            width: 48,
                            height: 48,
                            equals: () => true,
                        },
                        scaledSize: {
                            width: 48,
                            height: 48,
                            equals: () => true,
                        },
                    },
                }),
        };

        const algorithm = new SuperClusterAlgorithm({ maxZoom: 18 });

        this.markerCluster = new MarkerClusterer({
            map: this.map,
            markers: [...this.markers],
            algorithm,
            renderer,
        });
    }

    public geocodeFromString(address: string, countryCode: string): Observable<any> {
        return new Observable((observer) => {
            const geocoderRequest: google.maps.GeocoderRequest = {
                componentRestrictions: {
                    country: countryCode,
                },
                address,
                region: countryCode,
            };
            this.getGeocoderResponse(geocoderRequest, observer);
        });
    }

    private getGeocoderResponse(geocoderRequest: google.maps.GeocoderRequest, observer: Subscriber<any>) {
        const geocoder = new google.maps.Geocoder();
        return geocoder.geocode(geocoderRequest, (results, status) => {
            // Ésta funcionalidad debe ser ejecutada en el Zone de Angular ya que ocurre fuera del scope de Angular
            this.zone.run(() => {
                if (status === 'OK') {
                    observer.next(results[0]);
                    observer.complete();
                } else if (status === 'ZERO_RESULTS') {
                    observer.next(results);
                    observer.complete();
                } else {
                    observer.error();
                }
            });
        });
    }
}
